import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const FilterPanel = ({ isOpen, onClose, onApplyFilters, currentFilters }) => {
  const [filters, setFilters] = useState({
    priceRange: { min: '', max: '' },
    marketCap: '',
    sector: '',
    exchange: '',
    dividendYield: { min: '', max: '' },
    peRatio: { min: '', max: '' },
    volume: '',
    ...currentFilters
  });

  const marketCapOptions = [
    { value: '', label: 'All Market Caps' },
    { value: 'mega', label: 'Mega Cap (>₹16.6T)' },
    { value: 'large', label: 'Large Cap (₹830B-₹16.6T)' },
    { value: 'mid', label: 'Mid Cap (₹166B-₹830B)' },
    { value: 'small', label: 'Small Cap (₹25B-₹166B)' },
    { value: 'micro', label: 'Micro Cap (<₹25B)' }
  ];

  const sectorOptions = [
    { value: '', label: 'All Sectors' },
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'financials', label: 'Financials' },
    { value: 'consumer-discretionary', label: 'Consumer Discretionary' },
    { value: 'communication', label: 'Communication Services' },
    { value: 'industrials', label: 'Industrials' },
    { value: 'consumer-staples', label: 'Consumer Staples' },
    { value: 'energy', label: 'Energy' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'materials', label: 'Materials' }
  ];

  const exchangeOptions = [
    { value: '', label: 'All Exchanges' },
    { value: 'nasdaq', label: 'NASDAQ' },
    { value: 'nyse', label: 'NYSE' },
    { value: 'amex', label: 'AMEX' }
  ];

  const volumeOptions = [
    { value: '', label: 'Any Volume' },
    { value: '1m', label: 'Over 1M' },
    { value: '5m', label: 'Over 5M' },
    { value: '10m', label: 'Over 10M' },
    { value: '50m', label: 'Over 50M' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleRangeChange = (rangeKey, field, value) => {
    setFilters(prev => ({
      ...prev,
      [rangeKey]: {
        ...prev[rangeKey],
        [field]: value
      }
    }));
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      priceRange: { min: '', max: '' },
      marketCap: '',
      sector: '',
      exchange: '',
      dividendYield: { min: '', max: '' },
      peRatio: { min: '', max: '' },
      volume: ''
    };
    setFilters(resetFilters);
    onApplyFilters(resetFilters);
  };

  // Disable body scroll when filter panel is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    // Cleanup on unmount
    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-modal lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Filter Panel */}
      <div className={`
        fixed bottom-0 left-0 right-0 bg-card border-t border-border z-modal
        transform transition-transform duration-300 ease-out
        lg:relative lg:transform-none lg:border lg:rounded-lg lg:max-w-sm
        ${isOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'}
        max-h-[80vh] lg:max-h-none overflow-y-auto
      `}>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Filters</h3>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={handleResetFilters}>
                Reset
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Price Range */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Price Range</label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder="Min ($)"
                value={filters.priceRange.min}
                onChange={(e) => handleRangeChange('priceRange', 'min', e.target.value)}
              />
              <Input
                type="number"
                placeholder="Max ($)"
                value={filters.priceRange.max}
                onChange={(e) => handleRangeChange('priceRange', 'max', e.target.value)}
              />
            </div>
          </div>

          {/* Market Cap */}
          <div className="space-y-3">
            <Select
              label="Market Cap"
              options={marketCapOptions}
              value={filters.marketCap}
              onChange={(value) => handleFilterChange('marketCap', value)}
            />
          </div>

          {/* Sector */}
          <div className="space-y-3">
            <Select
              label="Sector"
              options={sectorOptions}
              value={filters.sector}
              onChange={(value) => handleFilterChange('sector', value)}
              searchable
            />
          </div>

          {/* Exchange */}
          <div className="space-y-3">
            <Select
              label="Exchange"
              options={exchangeOptions}
              value={filters.exchange}
              onChange={(value) => handleFilterChange('exchange', value)}
            />
          </div>

          {/* Volume */}
          <div className="space-y-3">
            <Select
              label="Volume"
              options={volumeOptions}
              value={filters.volume}
              onChange={(value) => handleFilterChange('volume', value)}
            />
          </div>

          {/* Dividend Yield Range */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Dividend Yield (%)</label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder="Min (%)"
                value={filters.dividendYield.min}
                onChange={(e) => handleRangeChange('dividendYield', 'min', e.target.value)}
              />
              <Input
                type="number"
                placeholder="Max (%)"
                value={filters.dividendYield.max}
                onChange={(e) => handleRangeChange('dividendYield', 'max', e.target.value)}
              />
            </div>
          </div>

          {/* P/E Ratio Range */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">P/E Ratio</label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder="Min"
                value={filters.peRatio.min}
                onChange={(e) => handleRangeChange('peRatio', 'min', e.target.value)}
              />
              <Input
                type="number"
                placeholder="Max"
                value={filters.peRatio.max}
                onChange={(e) => handleRangeChange('peRatio', 'max', e.target.value)}
              />
            </div>
          </div>

          {/* Apply Button */}
          <div className="pt-4 border-t border-border">
            <Button 
              onClick={handleApplyFilters}
              fullWidth
              className="h-12"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;